import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Tabs,
} from "@amzn/awsui-components-react/polaris";

import CustomMultiHybridInputAndSelect from "../Templates/components/CustomMultiHybridInputAndSelect";
import { useSuggestedLogins } from "src/hooks/useSuggestedLogins";

const CADENCE_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Annually", value: "ANNUALLY" },
  { label: "Workday-based", value: "WORKDAY_BASED" },
];

// Status options for the Mapping Configuration tab.
const STATUS_OPTIONS = [
  { label: "Success", value: "SUCCESS" },
  { label: "Error", value: "ERROR" },
  { label: "Warning", value: "WARNING" },
];

// Principal type options. "USER" maps to the value the shared component treats
// as an alias-autosuggest field ("UPLOADER"); "LDAP" is a plain text input.
// This mirrors how Frisco uploaders/approvers are entered across the app.
const PRINCIPAL_TYPE_OPTIONS = [
  { label: "USER", value: "UPLOADER" },
  { label: "LDAP", value: "UPLOADER_LDAP" },
];

/**
 * Dashboard "Control Panel" settings dialog (admin only).
 *
 * Owner/Viewer entry reuses the shared CustomMultiHybridInputAndSelect + the
 * useSuggestedLogins alias-fetch hook, so USER rows get the same phonetool alias
 * autosuggest used everywhere else and LDAP rows get a plain text input.
 */
const ControlPanelModal = ({ visible, onDismiss, onSave, dashboard }) => {
  const [dailyDigest, setDailyDigest] = useState(false);
  const [notifyDuplicate, setNotifyDuplicate] = useState(false);
  const [cadence, setCadence] = useState(null);

  // owners/viewers are arrays of { type, alias } managed by the shared component.
  const [perms, setPerms] = useState({ owners: [], viewers: [] });

  // Mapping Configuration: [{ key, value, status }]
  const [statusMappings, setStatusMappings] = useState([{ key: "", value: "", status: null }]);

  // Email Mapping: [{ alias, emails: [""] }]
  const [emailMappings, setEmailMappings] = useState([{ alias: "", emails: [""] }]);

  const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

  const getValues = (field) => perms[field] || [];
  const updateValue = (field, values) => setPerms((prev) => ({ ...prev, [field]: values }));
  const getTypeOptions = () => PRINCIPAL_TYPE_OPTIONS;

  // ---- Status mapping helpers ----
  const updateMapping = (index, patch) =>
    setStatusMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const removeMapping = (index) =>
    setStatusMappings((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ key: "", value: "", status: null }]
    );
  const addMapping = () =>
    setStatusMappings((prev) => [...prev, { key: "", value: "", status: null }]);

  // ---- Email mapping helpers ----
  const updateEmailAlias = (index, alias) =>
    setEmailMappings((prev) => prev.map((m, i) => (i === index ? { ...m, alias } : m)));
  const updateEmailAt = (mapIndex, emailIndex, value) =>
    setEmailMappings((prev) =>
      prev.map((m, i) =>
        i === mapIndex ? { ...m, emails: m.emails.map((e, j) => (j === emailIndex ? value : e)) } : m
      )
    );
  const addEmail = (mapIndex) =>
    setEmailMappings((prev) =>
      prev.map((m, i) => (i === mapIndex ? { ...m, emails: [...m.emails, ""] } : m))
    );
  const removeEmailMapping = (index) =>
    setEmailMappings((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ alias: "", emails: [""] }]
    );
  const addEmailMapping = () =>
    setEmailMappings((prev) => [...prev, { alias: "", emails: [""] }]);

  const handleSave = () => {
    onSave?.({
      notifications: { dailyDigest, notifyDuplicate },
      cadence: cadence?.value || null,
      owners: (perms.owners || []).filter((o) => o.type && (o.alias || "").trim()),
      viewers: (perms.viewers || []).filter((v) => v.type && (v.alias || "").trim()),
      statusMappings: statusMappings.filter(
        (m) => m.key.trim() && m.value.trim() && m.status
      ),
      emailMappings: emailMappings
        .filter((m) => m.alias.trim())
        .map((m) => ({ alias: m.alias.trim(), emails: m.emails.map((e) => e.trim()).filter(Boolean) })),
    });
  };

  const principalList = (field) => (
    <CustomMultiHybridInputAndSelect
      field={field}
      selectKey="type"
      inputKey="alias"
      placeholder="alias"
      selectHint="Choose type"
      getValues={getValues}
      updateValue={updateValue}
      getOptions={getTypeOptions}
      duplicatesNotAllowed={true}
      info=" "
      errorMessage="Type and alias input are mandatory"
      loginOptions={suggestedLogins}
      getLoginOptions={getSuggestedLoginsCaller}
      aliasesLoading={aliasesLoading}
    />
  );

  const controlsTab = (
    <SpaceBetween size="l">
      <div>
        <Header variant="h3">Notifications</Header>
        <SpaceBetween size="xs">
          <Checkbox checked={dailyDigest} onChange={({ detail }) => setDailyDigest(detail.checked)}>
            Enable daily digest emails
          </Checkbox>
          <Checkbox
            checked={notifyDuplicate}
            onChange={({ detail }) => setNotifyDuplicate(detail.checked)}
          >
            Notify duplicate uploads
          </Checkbox>
        </SpaceBetween>
      </div>

      <div>
        <Header variant="h3">Cadence</Header>
        <FormField label="Frequency">
          <Select
            placeholder="Select cadence"
            selectedOption={cadence}
            options={CADENCE_OPTIONS}
            onChange={({ detail }) => setCadence(detail.selectedOption)}
          />
        </FormField>
      </div>

      <div>
        <Header variant="h3">Dashboard Owner</Header>
        <FormField description="Type and alias input are mandatory">{principalList("owners")}</FormField>
      </div>

      <div>
        <Header variant="h3">Viewer</Header>
        <FormField description="Type and alias input are mandatory">{principalList("viewers")}</FormField>
      </div>

      <div>
        <Header variant="h3">Download</Header>
        <SpaceBetween direction="horizontal" size="xs">
          <Button iconName="download">Download CSV</Button>
          <Button iconName="download">Download Excel</Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  const mappingTab = (
    <SpaceBetween size="l">
      <div>
        <Header variant="h3">Status Mapping Configuration</Header>
        <SpaceBetween size="xs">
          <div style={{ display: "flex", gap: 8, paddingRight: 32 }}>
            <div style={{ flex: 1 }}>
              <Box variant="awsui-key-label">Key</Box>
            </div>
            <div style={{ width: 20 }} />
            <div style={{ flex: 1 }}>
              <Box variant="awsui-key-label">Value</Box>
            </div>
            <div style={{ width: 200 }}>
              <Box variant="awsui-key-label">Status</Box>
            </div>
          </div>
          {statusMappings.map((row, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Key"
                  value={row.key}
                  onChange={({ detail }) => updateMapping(index, { key: detail.value })}
                />
              </div>
              <span style={{ width: 12, textAlign: "center" }}>→</span>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Value"
                  value={row.value}
                  onChange={({ detail }) => updateMapping(index, { value: detail.value })}
                />
              </div>
              <div style={{ width: 200 }}>
                <Select
                  placeholder="Status"
                  selectedOption={row.status ? STATUS_OPTIONS.find((o) => o.value === row.status) : null}
                  options={STATUS_OPTIONS}
                  onChange={({ detail }) => updateMapping(index, { status: detail.selectedOption.value })}
                />
              </div>
              <Button
                variant="icon"
                iconName="close"
                ariaLabel="Remove mapping"
                onClick={() => removeMapping(index)}
              />
            </div>
          ))}
          <Button variant="inline-link" iconName="add-plus" onClick={addMapping}>
            Add mapping
          </Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  const emailTab = (
    <SpaceBetween size="l">
      <div>
        <Header variant="h3">Alias → Email Mapping</Header>
        <Box variant="p" color="text-body-secondary">
          Map each alias to one or more delivery email addresses for daily digest notifications.
        </Box>
      </div>

      <SpaceBetween size="s">
        {emailMappings.map((row, index) => (
          <Box key={index} variant="div" padding="s">
            <div
              style={{
                border: "1px solid #e9ebed",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <SpaceBetween size="xs">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      placeholder="Alias (e.g. johndoe or jane@partner.com)"
                      value={row.alias}
                      onChange={({ detail }) => updateEmailAlias(index, detail.value)}
                    />
                  </div>
                  <Button
                    variant="icon"
                    iconName="close"
                    ariaLabel="Remove alias mapping"
                    onClick={() => removeEmailMapping(index)}
                  />
                </div>
                <Box variant="awsui-key-label">Delivery emails:</Box>
                {row.emails.map((email, emailIndex) => (
                  <Input
                    key={emailIndex}
                    placeholder="recipient@example.com"
                    value={email}
                    onChange={({ detail }) => updateEmailAt(index, emailIndex, detail.value)}
                  />
                ))}
                <Button variant="inline-link" iconName="add-plus" onClick={() => addEmail(index)}>
                  Add delivery email
                </Button>
              </SpaceBetween>
            </div>
          </Box>
        ))}
        <Button variant="inline-link" iconName="add-plus" onClick={addEmailMapping}>
          Add alias mapping
        </Button>
      </SpaceBetween>

      <div
        style={{
          background: "#f0f7ff",
          border: "1px solid #cfe3ff",
          borderRadius: 8,
          padding: 16,
        }}
      >
        <SpaceBetween size="xs">
          <Box variant="strong">How it works</Box>
          <Box variant="small">
            • Amazon aliases (e.g. johndoe) — digest sent to mapped email instead of default
            @amazon.com inbox
          </Box>
          <Box variant="small">
            • External aliases (e.g. jane@partner.com) — digest sent to the specified delivery email(s)
          </Box>
          <Box variant="small">• Multiple delivery emails can be comma-separated</Box>
          <Box variant="small">
            • Actions (upload, approve) are always attributed to the logged-in Amazon alias —
            non-Amazon aliases are notification-only
          </Box>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Control Panel"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Tabs
        tabs={[
          { id: "controls", label: "Controls", content: controlsTab },
          { id: "mapping", label: "Mapping Configuration", content: mappingTab },
          { id: "email", label: "Email Mapping", content: emailTab },
        ]}
      />
    </Modal>
  );
};

export default ControlPanelModal;
